'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgChevronright, SvgGiftBox } from '@common/icon';
import { cn } from '@core/utils';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES } from '@/constants';
import type { AdTriggerPhase } from '@/modules/adpopcorn-ssp';
import { resolveAdTriggerNotice } from '../_utils/adTriggerNotice';
import { useLottoPool } from '../lucky-ticket/_hooks/useLottoPool';
import { useLottoState } from '../lucky-ticket/_hooks/useLottoState';
import { resolveLottoPotGauge } from '../lucky-ticket/_utils/resolveLottoPotGauge';
import { LottoPotGauge } from './LottoPotGauge';
const CARD_CLASS_NAME = 'flex flex-col gap-12 rounded-small bg-Gray-50 p-16 focus-visible:border-Gray-300 focus-visible:bg-Gray-100 xl:border xl:border-Gray-100 xl:bg-Gray-white';
const APPLY_BUTTON_CLASS_NAME = 'px-8 py-4 font-600 text-O-550 typo-caption1 disabled:cursor-not-allowed disabled:text-Gray-400';
const TICKET_COUNT_CLASS_NAME = 'font-600 text-Gray-600 typo-caption1';
const TicketCountLabel = () => {
  const $ = _c(5);
  const {
    data
  } = useLottoState();
  let t0;
  if ($[0] !== data.appliedTicketCount) {
    t0 = <span className="font-700 text-O-550">{data.appliedTicketCount}장</span>;
    $[0] = data.appliedTicketCount;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== data.requiredTicketCount || $[3] !== t0) {
    t1 = <span className={TICKET_COUNT_CLASS_NAME}>{t0} /{" "}{data.requiredTicketCount}장</span>;
    $[2] = data.requiredTicketCount;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const PoolPotGauge = t0 => {
  const $ = _c(8);
  const {
    showTicketCount
  } = t0;
  const {
    data
  } = useLottoPool();
  const t1 = data.potGauge;
  let t2;
  if ($[0] !== data.accumulatedAmount) {
    t2 = data.accumulatedAmount.toLocaleString();
    $[0] = data.accumulatedAmount;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `${t2}원`;
  let t4;
  if ($[2] !== showTicketCount) {
    t4 = showTicketCount ? <TicketCountLabel /> : undefined;
    $[2] = showTicketCount;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== data.potGauge || $[5] !== t3 || $[6] !== t4) {
    t5 = <LottoPotGauge {...t1} accumulatedAmountLabel={t3} leading={t4} />;
    $[4] = data.potGauge;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
const PoolPotGaugeFallback = t0 => {
  const $ = _c(5);
  const {
    showTicketCount
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = resolveLottoPotGauge({
      accumulatedAmount: 0,
      targetAmount: 0
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== showTicketCount) {
    t2 = showTicketCount ? <span className={TICKET_COUNT_CLASS_NAME}>-</span> : undefined;
    $[1] = showTicketCount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <LottoPotGauge {...t1} accumulatedAmountLabel="-" leading={t2} />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const LottoApplyButton = t0 => {
  const $ = _c(3);
  const {
    onApply,
    adTriggerPhase
  } = t0;
  const {
    data
  } = useLottoState();
  const t1 = !data.canApply || adTriggerPhase !== "ready";
  let t2;
  if ($[0] !== onApply || $[1] !== t1) {
    t2 = <button type="button" onClick={onApply} disabled={t1} className={APPLY_BUTTON_CLASS_NAME}>응모하기</button>;
    $[0] = onApply;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const LottoApplyTrailing = t0 => {
  const $ = _c(7);
  const {
    isGuest,
    onApply,
    adTriggerPhase
  } = t0;
  if (isGuest) {
    let t1;
    if ($[0] !== onApply) {
      t1 = <button type="button" onClick={onApply} className={APPLY_BUTTON_CLASS_NAME}>응모하기</button>;
      $[0] = onApply;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <button type="button" disabled={true} className={APPLY_BUTTON_CLASS_NAME}>응모하기</button>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <button type="button" disabled={true} className={APPLY_BUTTON_CLASS_NAME}>응모하기</button>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== adTriggerPhase || $[5] !== onApply) {
    t3 = <ErrorBoundary fallback={t1}><Suspense fallback={t2}><LottoApplyButton onApply={onApply} adTriggerPhase={adTriggerPhase} /></Suspense></ErrorBoundary>;
    $[4] = adTriggerPhase;
    $[5] = onApply;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const LottoCardChevron = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SvgChevronright className="size-16 text-Gray-400 transition group-focus-visible:translate-x-1 group-focus-visible:text-Gray-600 xl:group-hover:translate-x-1 xl:group-hover:text-Gray-600" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
interface LuckyLottoCardProps {
  isGuest: boolean;
  //* 제공하면 허브 변형(응모하기 버튼). 미제공하면 홈 변형(카드 전체가 허브 링크).
  onApply?: () => void;
  //* 허브 변형의 응모 광고 phase — pending/cooldown 동안 설명 줄이 안내 문구로 바뀌고 버튼이 잠긴다.
  adTriggerPhase?: AdTriggerPhase;
}

/**
 * 혜택홈·행운권 허브가 공유하는 단일 '행운권 로또' 카드.
 * 누적 상금 게이지는 useLottoPool(공동 값), 응모권 수는 useLottoState에서 파생한다.
 */
export const LuckyLottoCard = t0 => {
  const $ = _c(28);
  const {
    isGuest,
    onApply,
    adTriggerPhase: t1
  } = t0;
  const adTriggerPhase = t1 === undefined ? "ready" : t1;
  const isHub = onApply !== undefined;
  const showTicketCount = isHub && !isGuest;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SvgGiftBox className="size-24 shrink-0" originalFill={true} />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="font-700 text-Gray-900 typo-body4">행운권 로또</p>;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== adTriggerPhase) {
    t4 = resolveAdTriggerNotice(adTriggerPhase) ?? "\uBAA9\uD45C\uAE08\uC561\uC774 \uCC44\uC6CC\uC9C0\uBA74 \uC751\uBAA8\uD560 \uC218 \uC788\uC5B4\uC694";
    $[2] = adTriggerPhase;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <div className="flex flex-1 flex-col">{t3}<p className="break-keep font-600 text-Gray-600 typo-caption1">{t4}</p></div>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== adTriggerPhase || $[7] !== isGuest || $[8] !== onApply) {
    t6 = onApply ? <LottoApplyTrailing isGuest={isGuest} onApply={onApply} adTriggerPhase={adTriggerPhase} /> : <LottoCardChevron />;
    $[6] = adTriggerPhase;
    $[7] = isGuest;
    $[8] = onApply;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div className="flex items-center gap-16">{t2}{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== showTicketCount) {
    t8 = <PoolPotGaugeFallback showTicketCount={showTicketCount} />;
    $[13] = showTicketCount;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== showTicketCount) {
    t9 = <Suspense fallback={<PoolPotGaugeFallback showTicketCount={showTicketCount} />}><PoolPotGauge showTicketCount={showTicketCount} /></Suspense>;
    $[15] = showTicketCount;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <ErrorBoundary fallback={t8}>{t9}</ErrorBoundary>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t7) {
    t11 = <>{t7}{t10}</>;
    $[20] = t10;
    $[21] = t7;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const body = t11;
  if (isHub) {
    let t12;
    if ($[23] !== body) {
      t12 = <div className={CARD_CLASS_NAME}>{body}</div>;
      $[23] = body;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    return t12;
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn(CARD_CLASS_NAME, "group transition-colors active:bg-Gray-100 xl:hover:border-Gray-300 xl:hover:bg-Gray-100");
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== body) {
    t13 = <CustomLink href={ROUTES.BENEFIT_LUCKY_TICKET.pathname} className={t12}>{body}</CustomLink>;
    $[26] = body;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
};